/**
 * @fileoverview Access request validation schemas
 *
 * Schemas for the beta access request workflow including submission
 * and admin review operations.
 *
 * @module shared/schemas/access-requests
 */

import { z } from 'zod';
import { emailSchema, nameSchema } from './auth';

// =============================================================================
// Constants
// =============================================================================

/** Maximum reason length */
export const MAX_REASON_LENGTH = 500;

/** Access request status values */
export const ACCESS_REQUEST_STATUSES = ['pending', 'approved', 'rejected'] as const;

/** Type for access request status */
export type AccessRequestStatus = (typeof ACCESS_REQUEST_STATUSES)[number];

// =============================================================================
// Access Request Schemas
// =============================================================================

/**
 * Schema for submitting a beta access request.
 *
 * @example
 * ```typescript
 * const request = accessRequestSchema.parse({
 *   email: 'user@example.com',
 *   name: 'John Doe',
 *   reason: 'I want to use arml.ink for my marketing campaigns',
 * });
 * ```
 */
export const accessRequestSchema = z.object({
  email: emailSchema,
  name: nameSchema,
  reason: z
    .string()
    .max(MAX_REASON_LENGTH, `Reason must be ${MAX_REASON_LENGTH} characters or less`)
    .optional(),
});

/** TypeScript type for access request input */
export type AccessRequestInput = z.infer<typeof accessRequestSchema>;

/**
 * Schema for admin reviewing an access request.
 */
export const reviewAccessRequestSchema = z.object({
  status: z.enum(['approved', 'rejected']),
  adminNotes: z.string().max(500).optional(),
});

/** TypeScript type for review access request input */
export type ReviewAccessRequestInput = z.infer<typeof reviewAccessRequestSchema>;

/**
 * Query schema for listing access requests.
 */
export const listAccessRequestsQuerySchema = z.object({
  status: z.enum(ACCESS_REQUEST_STATUSES).optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});

/** TypeScript type for list access requests query */
export type ListAccessRequestsQuery = z.infer<typeof listAccessRequestsQuerySchema>;
